import { apiRequest } from "../services/apiRequest";
import {
  getSuperadminRoleModuleCatalogFallback,
  apiModuleStringToMenuPathKey,
} from "../utils/modulePermissionSidebar";

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

const PATHS = {
  SUPERADMIN_ROLES: "/account/admin/superadmin-roles/",
  SUPERADMIN_ROLE_MODULES: "/account/admin/superadmin-role-modules/",
  SUPERADMIN_ASSIGN_USER_ROLE: "/account/admin/users/superadmin-role/",
  USERS_LIST: "/account/users/list/",
};

// ---------------------------------------------------------------------------
// HTTP client (same pattern as MerchantsApi / AnnouncementsApi)
// ---------------------------------------------------------------------------

export const AdminRolesApi = {
  createSuperAdminRole: async (body) => {
    return await apiRequest("post", PATHS.SUPERADMIN_ROLES, body);
  },

  updateSuperAdminRole: async (roleId, body) => {
    return await apiRequest("patch", getSuperAdminRoleDetailPath(roleId), body);
  },

  listSuperAdminRoles: async (page = 1) => {
    const qs = new URLSearchParams({ page: String(page) }).toString();
    return await apiRequest(
      "get",
      `${PATHS.SUPERADMIN_ROLES}?${qs}`,
      undefined,
      { skipCache: true },
    );
  },

  listSuperadminRoleModules: async () => {
    return await apiRequest("get", PATHS.SUPERADMIN_ROLE_MODULES, undefined, {
      skipCache: true,
    });
  },

  assignSuperadminUserRole: async (body) => {
    return await apiRequest("post", PATHS.SUPERADMIN_ASSIGN_USER_ROLE, body);
  },

  patchSuperadminUserRole: async (body) => {
    return await apiRequest("patch", PATHS.SUPERADMIN_ASSIGN_USER_ROLE, body);
  },

  /**
   * Remove superadmin access. Body: `{ user_id, email? }` for active users, or
   * `{ invite_id }` to cancel a pending invite (no `user_id` yet).
   */
  revokeSuperadminUserRole: async (body) => {
    return await apiRequest("delete", PATHS.SUPERADMIN_ASSIGN_USER_ROLE, body, {
      skipCache: true,
    });
  },

  listUsersWithMarketRole: async (page = 1) => {
    const qs = new URLSearchParams({
      has_market_role: "true",
      page: String(page),
    }).toString();
    return await apiRequest("get", `${PATHS.USERS_LIST}?${qs}`, undefined, {
      skipCache: true,
    });
  },
};

// ---------------------------------------------------------------------------
// URL helpers
// ---------------------------------------------------------------------------

/** @param {string|number} roleId */
export function getSuperAdminRoleDetailPath(roleId) {
  const id = String(roleId ?? "").trim();
  const base = PATHS.SUPERADMIN_ROLES.replace(/\/+$/, "");
  return `${base}/${encodeURIComponent(id)}/`;
}

// ---------------------------------------------------------------------------
// Access / module catalog (UI + payloads)
// ---------------------------------------------------------------------------

/**
 * @typedef {{ moduleId: number, accessKey: string, label: string }} SuperadminModuleRow
 * @typedef {{ key: string, label: string }} SuperadminModuleAccessType
 */

/** UI access level → API `access` value sent in `module_permissions`. */
const UI_ACCESS_TO_API_KEY = {
  partial: "read_only",
  edit: "edit_only",
  full: "full_access",
};

/**
 * Maps API `module_access_types[].key` to UI levels used in checkboxes / accessByModule.
 * `edit` is the Editor level: full access without delete (API `edit_only`).
 * @param {string} apiKey
 * @returns {"none"|"partial"|"edit"|"full"}
 */
export function apiAccessKeyToUiAccess(apiKey) {
  const k = String(apiKey || "")
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_");
  if (k === "full_access" || k === "full" || k === "write" || k === "manage") {
    return "full";
  }
  if (k === "edit_only" || k === "no_delete" || k === "edit") {
    return "edit";
  }
  if (
    k === "read_only" ||
    k === "readonly" ||
    k === "read" ||
    k === "partial" ||
    k === "view"
  ) {
    return "partial";
  }
  return "none";
}

/**
 * Parses GET /account/admin/superadmin-role-modules/ body:
 * `{ module_access_types: [{ key, label, label_arabic }], modules: [{ id, key, label, label_arabic }] }`
 * @param {unknown} payload - axios `response.data`
 * @param {"ar"|"en"|string} [lang] - When Arabic, uses `label_arabic` / `labelArabic` when present.
 * @returns {{ moduleRows: SuperadminModuleRow[], moduleAccessTypes: SuperadminModuleAccessType[] }}
 */
export function parseSuperadminRoleModulesResponse(payload, lang = "en") {
  const useArabic = String(lang || "").toLowerCase().startsWith("ar");

  const accessTypesRaw = Array.isArray(payload?.module_access_types)
    ? payload.module_access_types
    : [];
  const moduleAccessTypes = accessTypesRaw
    .map((x) => {
      const key = String(x?.key ?? "").trim();
      if (!key) return null;
      const labelEn =
        String(x?.label ?? x?.key ?? "").trim() || key;
      const arRaw = x?.label_arabic ?? x?.labelArabic;
      const labelAr =
        arRaw != null && String(arRaw).trim() !== ""
          ? String(arRaw).trim()
          : "";
      const label =
        useArabic && labelAr !== "" ? labelAr : labelEn;
      return { key, label };
    })
    .filter(Boolean);

  const rawModules = Array.isArray(payload?.modules)
    ? payload.modules
    : Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.results)
        ? payload.results
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

  const seen = new Set();
  const moduleRows = [];
  for (const item of rawModules) {
    if (!item || typeof item !== "object") continue;
    const moduleId = Number(
      item.id ?? item.module_id ?? item.moduleId ?? item.pk,
    );
    if (!Number.isFinite(moduleId)) continue;
    const rawKey =
      item.key ??
      item.module ??
      item.module_key ??
      item.slug ??
      item.path ??
      "";
    let accessKey = rawKey
      ? apiModuleStringToMenuPathKey(String(rawKey))
      : "";
    if (!accessKey) accessKey = `m-${moduleId}`;
    const labelEn =
      String(
        item.label ?? item.module_name ?? item.name ?? item.title ?? accessKey,
      ).trim() || accessKey;
    const arRaw = item.label_arabic ?? item.labelArabic;
    const labelAr =
      arRaw != null && String(arRaw).trim() !== ""
        ? String(arRaw).trim()
        : "";
    const label =
      useArabic && labelAr !== "" ? labelAr : labelEn;
    if (seen.has(moduleId)) continue;
    seen.add(moduleId);
    moduleRows.push({ moduleId, accessKey, label });
  }

  return { moduleRows, moduleAccessTypes };
}

/**
 * @param {unknown} payload
 * @param {"ar"|"en"|string} [lang]
 * @returns {SuperadminModuleRow[]}
 */
export function parseSuperadminRoleModulesPayload(payload, lang) {
  return parseSuperadminRoleModulesResponse(payload, lang).moduleRows;
}

/**
 * Maps module rows to path→id pairs for role create / edit payloads.
 * @param {SuperadminModuleRow[] | null | undefined} moduleRows
 */
export function getSuperadminRoleModuleCatalog(moduleRows) {
  if (moduleRows?.length) {
    return moduleRows.map((r) => ({
      path: r.accessKey,
      module_id: r.moduleId,
    }));
  }
  return getSuperadminRoleModuleCatalogFallback();
}

function pathToModuleIdMap(moduleRows) {
  return new Map(
    getSuperadminRoleModuleCatalog(moduleRows).map((row) => [
      row.path,
      row.module_id,
    ]),
  );
}

function emptyAccessByPath(moduleRows) {
  const paths = getSuperadminRoleModuleCatalog(moduleRows).map((r) => r.path);
  return Object.fromEntries(paths.map((p) => [p, "none"]));
}

/**
 * POST /account/admin/superadmin-roles/ body (matches backend contract).
 * @param {{ roleName: string, description?: string, accessByModule: Record<string, string>, moduleRows?: { moduleId: number, accessKey: string, label: string }[] | null }} input
 * @returns {{ role_name: string, description: string, module_permissions: { module_id: number, access: "full_access"|"edit_only"|"read_only" }[] }}
 */
export function buildSuperAdminRoleCreateBody({
  roleName,
  description = "",
  accessByModule,
  moduleRows = null,
}) {
  const idByPath = pathToModuleIdMap(moduleRows);
  const module_permissions = [];
  for (const [path, level] of Object.entries(accessByModule || {})) {
    const access = UI_ACCESS_TO_API_KEY[level];
    if (!access) continue;
    const module_id = idByPath.get(path);
    if (module_id == null) continue;
    module_permissions.push({ module_id, access });
  }
  return {
    role_name: String(roleName || "").trim(),
    description: String(description ?? "").trim(),
    module_permissions,
  };
}

/**
 * Normalizes GET /account/admin/superadmin-roles/ body to an array of role objects.
 * @param {unknown} payload - axios `response.data`
 * @returns {object[]}
 */
export function parseSuperAdminRolesListResponse(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.roles)) return payload.roles;
  return [];
}

/**
 * Maps GET/POST superadmin role payload to UI table row.
 * Supports `module_permissions` as either `{ module_id, access }` or `{ module, access }`.
 * @param {object} apiRole
 * @param {{ moduleId: number, accessKey: string, label: string }[] | null} [moduleRows]
 */
export function mapSuperAdminRoleToRow(apiRole, moduleRows = null) {
  const hasCatalog = Boolean(moduleRows?.length);
  const accessByModule = hasCatalog ? emptyAccessByPath(moduleRows) : {};
  const idToPath = new Map(
    getSuperadminRoleModuleCatalog(moduleRows).map((c) => [
      c.module_id,
      c.path,
    ]),
  );

  for (const m of apiRole?.module_permissions || []) {
    let path = null;
    if (m?.module != null && String(m.module).trim() !== "") {
      path = apiModuleStringToMenuPathKey(String(m.module));
    } else if (m?.module_id != null) {
      path = idToPath.get(m.module_id) ?? null;
    }
    if (!path) continue;
    const ui = apiAccessKeyToUiAccess(m.access);
    accessByModule[path] = ui === "none" ? "partial" : ui;
  }

  const created = apiRole?.time_created
    ? Date.parse(apiRole.time_created)
    : Date.now();
  const displayName =
    String(apiRole?.role_name ?? apiRole?.role ?? "").trim() ||
    `Role ${apiRole?.id ?? ""}`;

  return {
    id: String(apiRole.id),
    name: displayName,
    description: apiRole.description || "",
    roleCode: apiRole.role != null ? String(apiRole.role) : undefined,
    accessByModule,
    createdAt: Number.isFinite(created) ? created : Date.now(),
  };
}

/**
 * User rows from GET /account/users/list/ (paged `results` or plain array).
 * @param {unknown} payload - axios `response.data`
 * @returns {object[]}
 */
export function parseAccountUsersListResponse(payload) {
  return parsePaginatedListResponse(payload).results;
}

/**
 * DRF-style paginated list: `{ count, results }` or plain array.
 * @param {unknown} payload - axios `response.data`
 * @returns {{ results: object[], count: number }}
 */
export function parsePaginatedListResponse(payload) {
  let results = [];
  if (Array.isArray(payload?.results)) {
    results = payload.results;
  } else if (Array.isArray(payload)) {
    results = payload;
  } else if (Array.isArray(payload?.data)) {
    results = payload.data;
  }

  const countRaw = payload?.count ?? payload?.total ?? payload?.total_count;
  const count =
    countRaw != null && Number.isFinite(Number(countRaw))
      ? Number(countRaw)
      : results.length;

  return { results, count };
}

/**
 * Build lookup: user `role` string (e.g. "3") or role row id → display name (`role_name` from roles API).
 * @param {Array<{ id?: string, name?: string, roleCode?: string }>} mappedRoleRows from `mapSuperAdminRoleToRow`
 * @returns {Map<string, string>}
 */
export function buildSuperadminRoleNameLookup(mappedRoleRows) {
  const m = new Map();
  if (!Array.isArray(mappedRoleRows)) return m;
  for (const r of mappedRoleRows) {
    const label = r?.name != null ? String(r.name).trim() : "";
    if (!label) continue;
    if (r.roleCode != null && String(r.roleCode).trim() !== "") {
      m.set(String(r.roleCode).trim(), label);
    }
    if (r.id != null && String(r.id).trim() !== "") {
      m.set(String(r.id).trim(), label);
    }
  }
  return m;
}

function apiRoleNameFromCamel(apiUser) {
  const n = apiUser?.roleName;
  if (n != null && String(n).trim() !== "") return String(n).trim();
  return "";
}

/**
 * Maps one user from `GET .../users/list/?has_market_role=true` into a table row.
 * Does not resolve superadmin catalog names — use `marketRoleKey` + roles list for display only.
 * @param {object} apiUser
 */
export function mapMarketRoleUserToRow(apiUser) {
  if (!apiUser || typeof apiUser !== "object") return null;
  const accessByModule = {};
  for (const m of apiUser.admin_role_module_permissions || []) {
    const path = apiModuleStringToMenuPathKey(String(m?.module ?? ""));
    if (!path) continue;
    const ui = apiAccessKeyToUiAccess(m.access);
    accessByModule[path] = ui === "none" ? "partial" : ui;
  }
  const createdRaw =
    apiUser.created_time ??
    apiUser.time_created ??
    apiUser.date_joined ??
    apiUser.created_at ??
    apiUser.created;
  let createdAt = null;
  if (createdRaw != null && String(createdRaw).trim() !== "") {
    const parsed = Date.parse(String(createdRaw));
    if (Number.isFinite(parsed)) createdAt = parsed;
  }

  const marketRoleKey =
    apiUser.role != null ? String(apiUser.role).trim() : "";

  const fromApiName =
    apiUser.role_name != null && String(apiUser.role_name).trim() !== ""
      ? String(apiUser.role_name).trim()
      : apiRoleNameFromCamel(apiUser);

  const email = String(apiUser.email_address ?? apiUser.email ?? "").trim();
  const apiUserId =
    apiUser.id != null && Number.isFinite(Number(apiUser.id))
      ? Number(apiUser.id)
      : null;
  const inviteId =
    apiUser.invite_id != null && Number.isFinite(Number(apiUser.invite_id))
      ? Number(apiUser.invite_id)
      : null;
  const rowId =
    apiUserId != null
      ? String(apiUserId)
      : inviteId != null
        ? `invite-${inviteId}`
        : email || "unknown";

  const statusRaw = apiUser.status;
  const status =
    statusRaw != null && String(statusRaw).trim() !== ""
      ? String(statusRaw).trim().toLowerCase()
      : "";

  return {
    id: rowId,
    apiUserId,
    inviteId,
    email,
    marketRoleKey,
    roleNameFromApi: fromApiName !== "" ? fromApiName : null,
    nameEnglish:
      apiUser.name_english != null ? String(apiUser.name_english).trim() : "",
    accessByModule,
    createdAt,
    status,
  };
}
